import * as React from 'react';
import { MapPin, Plus, Trash2 } from 'lucide-react';
import { type Address, type GeocodeSuggestion } from '@hch/shared';
import { Button } from '~/components/ui/button';
import {
  Badge,
  Card,
  CardContent,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Field,
  Input,
} from '~/components/ui/primitives';
import { EmptyState, LoadingState, PageHeader } from '~/components/shared/states';
import { ConfirmDialog } from '~/components/shared/confirm-dialog';
import { api } from '~/lib/api';
import { queryKeys, useAddresses, useCrudMutation, useGeocodeSearch } from '~/lib/queries';

export function ClientAddressesPage() {
  const { data, isLoading } = useAddresses();
  const addresses = data ?? [];

  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [toDelete, setToDelete] = React.useState<Address | null>(null);
  const [query, setQuery] = React.useState('');
  const [debounced, setDebounced] = React.useState('');
  const [picked, setPicked] = React.useState<GeocodeSuggestion | null>(null);

  React.useEffect(() => {
    const timer = setTimeout(() => setDebounced(query), 350);
    return () => clearTimeout(timer);
  }, [query]);

  const { data: suggestions } = useGeocodeSearch(debounced);

  const create = useCrudMutation({
    request: (input: Record<string, unknown>) => api.post('/me/addresses', input),
    invalidate: [queryKeys.addresses],
    successMessage: 'Adresse ajoutee.',
  });

  const setDefault = useCrudMutation({
    request: ({ id }: { id: string }) => api.put(`/me/addresses/${id}`, { isDefault: true }),
    invalidate: [queryKeys.addresses],
    successMessage: 'Adresse par defaut mise a jour.',
  });

  const remove = useCrudMutation({
    request: (id: string) => api.delete(`/me/addresses/${id}`),
    invalidate: [queryKeys.addresses],
    successMessage: 'Adresse supprimee.',
  });

  return (
    <div className="app-page">
      <PageHeader
        title="Mes adresses"
        description="L'adresse par defaut vous sera proposee lors de vos prochaines reservations."
        actions={
          <Button onClick={() => setDialogOpen(true)}>
            <Plus aria-hidden />
            Ajouter une adresse
          </Button>
        }
      />

      {isLoading ? <LoadingState /> : null}

      {!isLoading && addresses.length === 0 ? (
        <EmptyState icon={MapPin} title="Aucune adresse enregistree" />
      ) : null}

      <div className="flex flex-col gap-3">
        {addresses.map((address) => (
          <Card key={address.id} className="gap-3 py-4">
            <CardContent className="flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0 space-y-1">
                <div className="flex flex-wrap items-center gap-2">
                  <p className="font-medium">{address.label ?? 'Adresse'}</p>
                  {address.isDefault ? <Badge variant="secondary">Par defaut</Badge> : null}
                </div>
                <p className="text-sm text-muted-foreground">
                  {address.street}, {address.postalCode} {address.city}
                </p>
              </div>

              <div className="flex shrink-0 gap-2">
                {!address.isDefault ? (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => void setDefault.mutateAsync({ id: address.id })}
                  >
                    Definir par defaut
                  </Button>
                ) : null}
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setToDelete(address)}
                  aria-label="Supprimer cette adresse"
                >
                  <Trash2 className="text-destructive" />
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Ajouter une adresse</DialogTitle>
          </DialogHeader>

          <Field
            label="Rechercher"
            htmlFor="search-address"
            hint="Les adresses sont validees par la Base Adresse Nationale."
          >
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="12 rue de la Republique, Lyon"
              autoComplete="off"
            />
          </Field>

          {suggestions && suggestions.length > 0 ? (
            <ul className="flex max-h-56 flex-col gap-1 overflow-y-auto scroll-thin rounded-lg border border-border p-1">
              {suggestions.map((suggestion) => (
                <li key={`${suggestion.label}-${suggestion.lat}`}>
                  <button
                    type="button"
                    onClick={() => {
                      setPicked(suggestion);
                      setQuery(suggestion.label);
                    }}
                    className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-sm hover:bg-accent"
                  >
                    <MapPin className="size-4 shrink-0 text-muted-foreground" aria-hidden />
                    <span className="truncate">{suggestion.label}</span>
                  </button>
                </li>
              ))}
            </ul>
          ) : null}

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Annuler
            </Button>
            <Button
              disabled={!picked}
              loading={create.isPending}
              onClick={async () => {
                if (!picked) return;
                await create.mutateAsync({
                  label: 'Domicile',
                  street: picked.street,
                  city: picked.city,
                  postalCode: picked.postalCode,
                  country: picked.country,
                  lat: picked.lat,
                  lng: picked.lng,
                  isDefault: addresses.length === 0,
                });
                setDialogOpen(false);
                setPicked(null);
                setQuery('');
              }}
            >
              Ajouter
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={!!toDelete}
        onOpenChange={(open) => !open && setToDelete(null)}
        title="Supprimer cette adresse ?"
        description="Vos interventions passees a cette adresse restent consultables."
        variant="destructive"
        confirmLabel="Supprimer"
        loading={remove.isPending}
        onConfirm={async () => {
          if (!toDelete) return;
          await remove.mutateAsync(toDelete.id);
          setToDelete(null);
        }}
      />
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// Ma fiche
// ═══════════════════════════════════════════════════════════════════════════════
